import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { PaymentProcessingType } from "../type/PaymentProcessingType";
import { gsap } from "gsap";
import { GameFont } from "../../../common/GameFont";
import { IPaymentProcessingController } from "../controllers/IPaymentProcessingController";
import { Erc20BaseContent } from "../components/Erc20BaseContent";
import { PaymentProcessingViewState } from "../type/PaymentProcessingViewState";
import { PaymentProcessingPopupSceneOptions } from "../scenes/PaymentProcessingPopupSceneOptions";
import { Erc20PaymentProcessingResourcePackage } from "../resources/Erc20PaymentProcessingResourcePackage";
import { PaymentResult } from "../../../../../web3/PaymentResult";
import { Web3Model } from "../../../../../web3/models/Web3Model";
import { CancellationToken } from "../../../../../shared/async/CancellationToken";

export class PaymentProcessingPopupView extends BaseScalableView<PaymentResult> {
    private readonly animations: gsap.core.Timeline;

    private viewBackground: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private header: Text;
    private closeButton: ButtonWithScaleAnimation;
    private erc20BaseContent: Erc20BaseContent;

    public constructor(
        operationControls: PromiseCompletionSource<PaymentResult>,
        canvasScaler: ICanvasScaler,
        private readonly commonResources: CommonResourcesPackage,
        private readonly erc20Resources: Erc20PaymentProcessingResourcePackage,
        private readonly paymentController: IPaymentProcessingController,
        private readonly options: PaymentProcessingPopupSceneOptions<PaymentProcessingType>,
        private readonly web3Model: Web3Model,
        private readonly cancellationToken: CancellationToken,
        private readonly logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.animations = new gsap.core.Timeline({ paused: true });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.show();
        this.removeChild(inputBlocker);
    }

    private build(): void {
        this.viewBackground = new Sprite(this.erc20Resources.assets_views_erc_20_payment_processing_bg_png);
        this.viewBackground.eventMode = "static";
        this.leftLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight.scale.x = -1;
        this.header = new Text(`BUY ${this.options.type === PaymentProcessingType.STORE_PACKAGE ?  "ROLL\nPACKAGE" : "BUILDING"} \nWITH ${this.options.tokenSymbol}`, new HeaderTextStyle());
        this.header.skew._x = -0.087;
        this.header.angle = -5;
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.onUp.add(this.onCloseButtonClickHandle, this);
        this.erc20BaseContent = new Erc20BaseContent(this.commonResources, this.erc20Resources, this.options, this.web3Model);
        this.erc20BaseContent.onBuyClick.add(this.onBuyButtonClickHandle, this);
        this.addChild(
            this.viewBackground,
            this.leftLight,
            this.rightLight,
            this.header,
            this.erc20BaseContent,
            this.closeButton
        );
    }

    private async onCloseButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.hide();
        this.removeChild(inputBlocker);
        this.operationControls.complete(PaymentResult.CANCELLED);
    }

    private async onBuyButtonClickHandle(): Promise<void> {
        try {
            const erc20Balance = this.web3Model.isTargetChain(this.options.chain) ? Number(this.web3Model.erc20BalancesOnBase.get(this.options.erc20Address)): Number(this.web3Model.erc20BalancesOnL1.get(this.options.erc20Address))
            if (erc20Balance < this.options.price) {
                this.erc20BaseContent.switchState(PaymentProcessingViewState.NOT_ENOUGH_TOKENS);
            } else {
                this.erc20BaseContent.switchState(PaymentProcessingViewState.PAYMENT_PROCESSING);
                const paymentResult = await this.paymentController.buyStoreItem(this.options.chain, this.options.erc20Address, this.cancellationToken);
                this.operationControls.complete(paymentResult);
            }
        } catch (error: unknown) {
            this.logger.error("Payment processing exception: ", error);
        }
    }

    private async show(): Promise<void> {
        this.animations.fromTo(this.header.position, { y: this.header.position.y + 300 }, { y: this.header.position.y, ease: 'power1.out', duration: 0.3 }, '<');
        this.animations.fromTo(this.header, { alpha: 0 }, { alpha: 1, ease: 'power1.out', duration: 0.3 }, '<');
        this.animations.fromTo(this.closeButton, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: 0.3 }, '<');
        this.animations.fromTo(this.erc20BaseContent, { alpha: 0 }, { alpha: 1, ease: 'power1.in', duration: 0.3 }, '<50%');
        await this.animations.play();
    }

    private async hide(): Promise<void> {
        await this.animations.reverse();
    }

    private layout(width: number, _height: number): void {
        this.leftLight.position.set(0, 600);
        this.rightLight.position.set(width, 600);
        this.header.position.set(width * 0.5 - this.header.width * 0.5, (this.options.type === PaymentProcessingType.STORE_PACKAGE ? 451 : 506) - this.header.height * 0.5);
        this.closeButton.position.set(375, 1535);
        this.erc20BaseContent.position.set(width * 0.5 - this.erc20BaseContent.width * 0.5, 775);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.erc20BaseContent.onBuyClick.remove(this.onBuyButtonClickHandle, this);
        this.closeButton.onUp.remove(this.onCloseButtonClickHandle, this);
        this.animations.kill();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#a8fd09",
                "#15ce14"
            ],
            fillGradientStops: [0.1],
            fontSize: 148,
            align: "center",
            leading: -18
        });
    }
}
